import type { Scope } from "@storytell/cp-client";
import { createStore } from "solid-js/store";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { onCleanup, onMount } from "solid-js";

export const createChatServiceScope = (deps: {
  projectId: () => string | undefined;
}) => {
  // Scope management
  const [state, setState] = createStore({
    allKnowledge: false,
    concepts: [] as string[],
    assets: [] as string[],
    /**
     * Special holder for assets that are being uploaded.
     * They do not have an ID yet, but we need to show them in the scope.
     */
    uploadingAssets: [] as { id: string; filename: string; status: "uploading" | "error" }[],
  });

  // --- Event Subscriptions ---
  onMount(() => {
    const unsubscribeAdded = subscribeToGlobalEvent("added-mention-to-prompt-bar", (event) => {
      if (event.detail.id.startsWith("asset_")) {
        addAsset(event.detail.id);
      } else if (event.detail.id.startsWith("concept_")) {
        addConcept(event.detail.id);
      }
    });
    const unsubscribeRemoved = subscribeToGlobalEvent("removed-mention-from-prompt-bar", (event) => {
      if (event.detail.id.startsWith("asset_")) {
        removeAsset(event.detail.id);
      } else if (event.detail.id.startsWith("concept_")) {
        removeConcept(event.detail.id);
      }
    });
    onCleanup(() => {
      unsubscribeAdded();
      unsubscribeRemoved();
    });
  });

  const addAsset = (assetId: string) => {
    if (state.assets.includes(assetId)) return;
    setState("assets", (prev) => [...prev, assetId]);
  };
  const addUploadingAsset = (filename: string) => {
    // Random enough id to avoid collisions
    const assetId = `temp_${performance.now()}_${Math.random().toString(36).substring(2, 9)}`;
    if (state.uploadingAssets.some((a) => a.id === assetId)) return;
    setState("uploadingAssets", (prev) => [...prev, { id: assetId, filename, status: "uploading" }]);
    addAsset(assetId);
    return assetId;
  };
  const updateUploadingAssetStatus = (assetId: string, status: "uploading" | "error") => {
    setState("uploadingAssets", (prev) => prev.map((a) => (a.id === assetId ? { ...a, status } : a)));
  };
  const removeUploadingAsset = (assetId: string) => {
    setState("uploadingAssets", (prev) => prev.filter((a) => a.id !== assetId));
    removeAsset(assetId);
  };
  const removeAsset = (assetId: string) => {
    setState("assets", (prev) => prev.filter((id) => id !== assetId));
    dispatchGlobalEvent("removed-item-from-prompt-scope", { id: assetId });
  };
  const addConcept = (conceptId: string) => {
    if (state.concepts.includes(conceptId)) return;
    setState("concepts", (prev) => [...prev, conceptId]);
  };
  const removeConcept = (conceptId: string) => {
    setState("concepts", (prev) => prev.filter((id) => id !== conceptId));
    dispatchGlobalEvent("removed-item-from-prompt-scope", { id: conceptId });
  };

  const clearAll = () => {
    const toRemove = [...state.assets, ...state.concepts];
    setState({
      allKnowledge: false,
      assets: [],
      concepts: [],
    });
    for (const id of toRemove) {
      dispatchGlobalEvent("removed-item-from-prompt-scope", {
        id,
      });
    }
  };

  const assets = () => state.assets;
  const concepts = () => state.concepts;
  const allKnowledge = () => state.allKnowledge;
  const uploadingAssets = () => state.uploadingAssets;

  const setAllKnowledge = (allKnowledge: boolean) => {
    setState("allKnowledge", allKnowledge);
  };

  const getScopeForPromptSubmit = (): Scope.Scope => {
    const base: Scope.Scope = {
      $typeName: "query.v3.Scope",
      textAttachmentList: [],
      attachmentIdList: [],
      assetIdList: [],
      artifactIdList: [],
      labelIdList: [],
      conceptIdList: [],
      projectIdList: [],
      workspaceId: "",
      organizationId: "",
    };
    base;
    if (state.allKnowledge && deps.projectId()) {
      return {
        ...base,
        assetIdList: [],
        conceptIdList: [],
        projectIdList: [deps.projectId()!],
      };
    }

    return {
      ...base,
      assetIdList: state.assets,
      conceptIdList: state.concepts,
      projectIdList: [],
    };
  };

  return {
    assets,
    addUploadingAsset,
    concepts,
    allKnowledge,
    setAllKnowledge,
    addAsset,
    removeAsset,
    clearAll,
    addConcept,
    removeConcept,
    getScopeForPromptSubmit,
    uploadingAssets,
    removeUploadingAsset,
    updateUploadingAssetStatus,
  };
};
